import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, RefreshCw, Activity } from 'lucide-react';

const AptiviewAppStatus = ({ compact = false }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);

  const checkHealth = async () => {
    const interviewUiUrl = process.env.REACT_APP_INTERVIEW_UI_URL;

    if (!interviewUiUrl) {
      setStatus(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const baseUrl = interviewUiUrl.replace(/\/$/, '');
      const healthUrl = `${baseUrl}/api/health`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(healthUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setHealthData(data);
        setStatus(true);
      } else {
        setStatus(false);
        setHealthData(null);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Health check timeout');
      } else {
        console.error('Error checking Interview App health:', error);
      }
      setStatus(false);
      setHealthData(null);
    } finally {
      setLoading(false);
      setLastChecked(new Date());
    }
  };

  useEffect(() => {
    checkHealth();
  }, []);

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const statusLabel = loading && status === null
    ? t('companyAdmins.checking')
    : status
      ? t('companyAdmins.workingNormally')
      : t('companyAdmins.notWorkingFine');

  const statusColor = loading && status === null
    ? 'text-gray-500'
    : status
      ? 'text-green-700'
      : 'text-red-700';

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50">
            <Activity className="h-4 w-4 text-slate-500" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">{t('companyAdmins.interviewApp')}</p>
            <p className="text-[11px] text-gray-500">{t('companyAdmins.serviceStatus')}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {loading && status === null ? (
            <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />
          ) : status ? (
            <CheckCircle className="h-4 w-4 text-green-500" />
          ) : (
            <XCircle className="h-4 w-4 text-red-500" />
          )}
          <span className={`text-xs font-medium ${statusColor}`}>{statusLabel}</span>
          {status && healthData?.uptime !== undefined && (
            <span className="hidden text-[11px] text-gray-400 sm:inline">
              {t('companyAdmins.uptime')} {formatUptime(healthData.uptime)}
            </span>
          )}
          {lastChecked && (
            <span className="hidden text-[11px] text-gray-400 md:inline">
              {t('companyAdmins.lastChecked')} {lastChecked.toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={checkHealth}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            {loading ? t('companyAdmins.checking') : t('companyAdmins.checkAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-slate-50 p-2">
            <Activity className="h-5 w-5 text-slate-500" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{t('companyAdmins.interviewApp')}</h3>
            <p className="text-sm text-gray-600">{t('companyAdmins.serviceStatus')}</p>
          </div>
        </div>
        {loading ? (
          <RefreshCw className="h-5 w-5 text-gray-400 animate-spin" />
        ) : status ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : (
          <XCircle className="h-5 w-5 text-red-500" />
        )}
      </div>

      {loading && status === null ? (
        <div className="space-y-2">
          <div className="h-4 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
        </div>
      ) : status ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-500" />
            <span className="text-sm font-medium text-green-700">{t('companyAdmins.workingNormally')}</span>
          </div>
          {healthData && (
            <div className="mt-3 space-y-1.5 border-t border-gray-100 pt-3">
              {healthData.status && (
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{t('companyAdmins.status')}</span>
                  <span className="font-medium uppercase text-green-600">{healthData.status}</span>
                </div>
              )}
              {healthData.uptime !== undefined && (
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{t('companyAdmins.uptime')}</span>
                  <span className="font-medium">{formatUptime(healthData.uptime)}</span>
                </div>
              )}
            </div>
          )}
          {lastChecked && (
            <div className="mt-2 text-xs text-gray-500">
              {t('companyAdmins.lastChecked')} {lastChecked.toLocaleTimeString()}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-red-500" />
            <span className="text-sm font-medium text-red-700">{t('companyAdmins.notWorkingFine')}</span>
          </div>
          <button
            type="button"
            onClick={checkHealth}
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>{t('companyAdmins.checking')}</span>
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                <span>{t('companyAdmins.checkAgain')}</span>
              </>
            )}
          </button>
          {lastChecked && (
            <div className="text-center text-xs text-gray-500">
              {t('companyAdmins.lastChecked')} {lastChecked.toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AptiviewAppStatus;
